import { Analytics } from '@cobliteam/analytics';
import { SessionManager } from '@cobliteam/api-lib/src/account/session/SessionManager';
import { clearLdBootContext } from '@cobliteam/hooks/src/useFeatureFlags/ldBootContext';
import { shutdownIntercom } from '@cobliteam/hooks/src/useIntercom';
import useAuthStore from '@cobliteam/hooks/src/useSession/store/useAuthStore';
import { Environment } from '@cobliteam/react-environments';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { clear as idbClear } from 'idb-keyval';
import { toast } from 'sonner';

const env = Environment.getInstance().getEnvironment();
const sessionManager = new SessionManager({ host: env.herbieApiHost });

/**
 * Logout completo: limpa cache do React Query, IndexedDB, chama `/account/logout` (backend +
 * deleteAuthCookies), reseta Analytics e dá reset atômico no `useAuthStore` (session + userData
 * limpos num único write). Preserva o comportamento do antigo `requestLogoutSaga`.
 */
const useLogout = () => {
  const queryClient = useQueryClient();
  const reset = useAuthStore((s) => s.reset);

  return useMutation({
    mutationFn: async () => {
      // Chama /account/logout ANTES de limpar caches — assim, se a rede falhar, o usuário
      // ainda tem o app funcional para tentar de novo. idbClear rodava em paralelo com o
      // request antes; agora roda depois para evitar refetch de useSessionQuery (ainda montado
      // em PrivateRouterProvider) com cookies válidos, que repopularia o auth store.
      await sessionManager.logout();
      await idbClear();
      clearLdBootContext();
      shutdownIntercom();
      Analytics.reset();
    },
    onSuccess: () => {
      // Ordem importante: reset() primeiro desmonta a árvore privada; só então limpa o cache.
      reset();
      queryClient.removeQueries();
    },
    onError: () => toast.error('Houve um erro ao efetuar o logout, recarregue a página e tente novamente.'),
  });
};

export default useLogout;
